import { useState, useEffect } from 'react';
import { NotificationsService } from '../api/notifications';
import { Notification } from '../api/notifications/types';

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentNotificationIndex, setCurrentNotificationIndex] = useState(0);
  const [dismissedIds, setDismissedIds] = useState<string[]>(() => {
    try {
      const dismissed = localStorage.getItem('dismissed-notifications');
      const parsed = dismissed ? JSON.parse(dismissed) : [];
      return Array.isArray(parsed) ? parsed.map((v: unknown) => String(v)) : [];
    } catch {
      return [];
    }
  });

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await NotificationsService.getNotifications();
        const validNotifications = (response?.notifications || []).filter(
          (n) => n && n.id && n.content && !dismissedIds.includes(String(n.id)),
        );
        setNotifications(validNotifications);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    fetchNotifications();
  }, []);

  // Helper functions
  const saveDismissedIds = (ids: string[]) => {
    setDismissedIds(ids);
    localStorage.setItem('dismissed-notifications', JSON.stringify(ids));
  };

  const dismissNotification = (notificationId: string) => {
    const newDismissedIds = [...dismissedIds, String(notificationId)];
    saveDismissedIds(newDismissedIds);

    const remainingNotifications = notifications.filter((n) => n?.id !== notificationId);
    setNotifications(remainingNotifications);

    if (
      currentNotificationIndex >= remainingNotifications.length &&
      remainingNotifications.length > 0
    ) {
      setCurrentNotificationIndex(remainingNotifications.length - 1);
    }
  };

  const goToNextNotification = () => {
    setCurrentNotificationIndex((prev) => (prev + 1) % notifications.length);
  };

  const goToPreviousNotification = () => {
    setCurrentNotificationIndex((prev) => (prev - 1 + notifications.length) % notifications.length);
  };

  const currentNotification = notifications[currentNotificationIndex];
  const hasNotifications = notifications.length > 0;

  return {
    notifications,
    currentNotification,
    currentNotificationIndex,
    hasNotifications,
    dismissNotification,
    goToNextNotification,
    goToPreviousNotification,
  };
}
